<div class="min-h-screen bg-bg flex flex-col pb-7">
  <!-- Header - Full Width -->
  <header class="bg-primary text-white shadow-lg w-full">
    <div class="pl-5 pr-4 sm:pr-6 lg:pr-8">
      <div class="flex items-center h-16">
        <!-- Left: menu + logo -->
        <div class="flex items-center gap-2 flex-shrink-0">
          <!-- Menu Button -->
          <button (click)="toggleSidebar()"
            class="flex items-center justify-center w-10 h-10 rounded-md hover:bg-white/10 transition-colors"
            aria-label="Toggle sidebar">
            <fa-icon [icon]="faBars" class="w-5 h-5 text-white/80"></fa-icon>
          </button>

          <!-- Logo -->
          <img src="assets/images/logo.png" alt="ISAMED Logo" class="h-12 w-auto object-contain">
        </div>

        <!-- Center: Application title -->
        <div class="flex-1 flex items-center justify-center px-4">
          <div class="inline-flex items-center gap-3">
            <h1 class="inline-flex items-baseline gap-2 text-base sm:text-lg lg:text-xl font-medium text-white/95">
              <span class="uppercase font-semibold tracking-[0.4em] text-white">
                ISAMED
              </span>
              <span class="font-medium tracking-wider text-white/90">
                Practice Management System
              </span>
            </h1>
          </div>
        </div>

        <!-- DoseSpot notifications (hidden when user lacks EMR access) -->
        <div *ngIf="canAccessEMR" #dosespotBellRef class="relative flex-shrink-0 pr-1 sm:pr-2">
          <button
            type="button"
            (click)="toggleDosespotPanel()"
            class="relative flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/10 transition-colors"
            [attr.aria-expanded]="isDosespotPanelOpen"
            aria-haspopup="true"
            aria-label="DoseSpot notifications"
            title="DoseSpot notifications">
            <fa-icon [icon]="faBell" class="w-5 h-5 text-white/90"></fa-icon>
            <span
              *ngIf="unreadDosespotCount > 0"
              class="absolute -top-0.5 -right-0.5 min-w-[1.125rem] h-[1.125rem] px-1 rounded-full bg-amber-400 text-[10px] font-bold text-slate-900 flex items-center justify-center leading-none shadow-sm">
              {{ unreadDosespotCount > 99 ? '99+' : unreadDosespotCount }}
            </span>
          </button>

          <div
            *ngIf="isDosespotPanelOpen"
            class="absolute right-0 top-full mt-2 w-[min(22rem,calc(100vw-2rem))] max-h-[min(24rem,70vh)] bg-white rounded-lg shadow-xl overflow-hidden flex flex-col z-[9999]"
            role="menu"
            aria-label="DoseSpot notification list">
            <div class="px-4 py-3 border-b border-slate-100 bg-slate-50">
              <p class="text-sm font-semibold text-slate-800">DoseSpot</p>
              <p class="text-[11px] text-slate-500">Recent prescription updates (last 20)</p>
            </div>

            <div *ngIf="dosespotNotificationsLoading" class="px-4 py-6 text-center text-sm text-slate-500">
              <i class="fas fa-spinner fa-spin text-primary mr-2"></i>
              Loading notifications…
            </div>

            <div
              *ngIf="!dosespotNotificationsLoading && dosespotNotifications.length === 0"
              class="px-4 py-6 text-center text-sm text-slate-500">
              No recent notifications.
            </div>

            <ul
              *ngIf="!dosespotNotificationsLoading && dosespotNotifications.length > 0"
              class="overflow-y-auto py-1 divide-y divide-slate-100">
              <li
                *ngFor="let n of dosespotNotifications; trackBy: trackDosespotNotification"
                class="px-4 py-2.5 hover:bg-slate-50 transition-colors"
                [class.opacity-60]="isDosespotRowOpening(n)"
                [class.cursor-pointer]="!!n.elabId"
                [class.cursor-default]="!n.elabId"
                [attr.title]="n.elabId ? 'Open in DoseSpot (eLab ' + n.elabId + ')' : null"
                (click)="onDosespotNotificationClick(n, $event)">
                <div class="flex items-start justify-between gap-2">
                  <p class="text-sm font-medium text-slate-800 leading-snug flex-1">
                    {{ n.statusDetails || n.summary || n.eventType || 'Notification' }}
                  </p>
                  <span
                    *ngIf="n.eventType"
                    class="shrink-0 text-[9px] uppercase tracking-wide font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                    {{ n.eventType }}
                  </span>
                </div>
                <p *ngIf="n.patientName" class="text-xs text-slate-600 mt-0.5 truncate">
                  {{ n.patientName }}
                </p>
                <p class="text-[11px] text-slate-500 mt-0.5 font-mono truncate">
                  <span *ngIf="n.elabId">eLab ID: {{ n.elabId }}</span>
                  <span *ngIf="!n.elabId && n.patientId">Patient ID: {{ n.patientId }}</span>
                  <span *ngIf="n.prescriptionId"> · Rx {{ n.prescriptionId }}</span>
                  <span *ngIf="n.prescriptionStatusLabel && !n.statusDetails"> · {{ n.prescriptionStatusLabel }}</span>
                </p>
                <p *ngIf="n.createdAt" class="text-[10px] text-slate-400 mt-1">
                  {{ formatDosespotTime(n.createdAt) }}
                </p>
                <p *ngIf="isDosespotRowOpening(n)" class="text-[10px] text-primary mt-1">
                  <i class="fas fa-spinner fa-spin mr-1"></i>
                  Opening DoseSpot…
                </p>
              </li>
            </ul>
          </div>
        </div>

        <!-- Right Side Actions: User Profile Dropdown -->
        <div #profileMenuRef class="relative pl-4 border-l border-white/10 flex-shrink-0">
          <!-- Trigger: Restored Custom Profile UI -->
          <div (click)="toggleProfileMenu()"
            class="group flex items-center gap-3 p-1.5 pl-3 rounded-full hover:bg-white/10 transition-all duration-200 border border-transparent hover:border-white/10 cursor-pointer select-none">

            <!-- User Info (Left) -->
            <div class="hidden sm:flex flex-col items-end min-w-[120px]">
              <span
                class="text-xs font-semibold text-white tracking-wide group-hover:text-white/90 transition-colors truncate max-w-[160px]">
                {{ userFullName || 'My Profile' }}
              </span>
              <span class="text-[10px] text-white/70 font-medium truncate max-w-[160px]">
                {{ userRoleLabel || ' ' }}
              </span>
            </div>

            <!-- Avatar (Right) -->
            <div class="relative">
              <div
                class="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center ring-1 ring-white/20 group-hover:bg-white/20 transition-all">
                <fa-icon [icon]="faUser" class="w-5 h-5 text-white"></fa-icon>
              </div>
            </div>
          </div>

          <!-- Dropdown Menu -->
          <div *ngIf="isProfileMenuOpen"
            class="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-xl py-2 overflow-hidden animate-in fade-in slide-in-from-top-2"
            style="position:absolute; z-index: 9999;">

            <button (click)="onProfileAction('profile')"
              class="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-[#f0f9ff] transition-colors flex items-center gap-2">
              <fa-icon [icon]="faUser" class="w-4 h-4"></fa-icon>
              My Profile
            </button>

            <div class="h-px bg-gray-100 my-1"></div>

            <button (click)="onProfileAction('logout')"
              class="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  </header>

  <!-- Content Area with Sidebar -->
  <div class="flex flex-1 overflow-hidden">
    <!-- Sidebar Component -->
    <app-sidebar [isOpen]="isSidebarOpen" (openSidebar)="onSidebarOpenRequest()"></app-sidebar>

    <!-- Main Content (scroll container for authenticated area; scroll-to-top uses this id when targetId is omitted) -->
    <main id="app-main-scroll" class="flex-1 overflow-auto relative bg-bg">
      <!-- Hide the previous screen immediately on menu change; show loader until the new route is ready. -->
      <div *ngIf="isRouteLoading" class="flex min-h-full items-center justify-center py-16">
        <div class="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm">
          <i class="fas fa-spinner fa-spin text-primary"></i>
          <span>Loading...</span>
        </div>
      </div>
      <div [class.hidden]="isRouteLoading">
        <router-outlet></router-outlet>
        <app-patient-detail-overlay-host></app-patient-detail-overlay-host>
      </div>
    </main>
  </div>

  <app-status-footer></app-status-footer>
</div>